import React, { useEffect } from 'react';
import { Alert, Button, SkeletonBlock, SkeletonContent, Typography, } from '@one-thd/sui-atomic-components';
import { useInstantCheckout } from '../../../hooks/use-instant-checkout';
import { InstantCheckoutToggle } from '../instant-checkout-toggle/instant-checkout-toggle';
import { DeliveryAddress } from '../delivery-address/delivery-address';
import { PaymentMethod } from '../payment-method/payment-method';
import { usePayments } from '../../../hooks/use-payments';
import { INSTANT_CHECKOUT_ENROLLMENT_SUCCESS } from '../../../constants/response-success-messages';
import { ERR_GENERIC } from '../../../constants/response-error-messages';
import { TAlertStatus, TAlertType, } from '../../../models/common/alert-messages';
import { InstantCheckoutAlert } from '../instant-checkout-alert/instant-checkout-alert';
import { checkForLoyaltyAndBuildAccountObject } from '../../../utils/instant-checkout-helpers';
import { b2cAddressesApi } from '../../../api/b2c/addresses';
import { b2cPaymentsApi } from '../../../api/b2c/payments';
import { useAddresses } from '../../../hooks/use-addresses';
import { INSTANT_CHECKOUT_NO_DEFAULT_PAYMENT_MSG, INSTANT_CHECKOUT_NO_DEFAULT_ADDRESS_MSG, INSTANT_CHECKOUT_NO_DEFAULT_ADDRESS_AND_PAYMENT_MSG, } from '../../../constants/response-warning-messages';
import { EnrolledSummarySection } from './enrolled-summary-section';
import { NoMethodCard } from '../no-method-card/noMethodCard';
import { setToggleEnrollmentAnalytics, setChangeEnrolledDetailsAnalytics, setPartialEnrolledAnalytics, } from '../../../utils/instant-checkout-analytics';
import { setPageInfo, track } from '../../../utils/analytics';
import { INSTANT_CHECKOUT_POST_PAGE_ERROR } from '../../../constants/analytics';
const defaultPageAnalyticstPayload = {
    pageSection: 'instant checkout enrolled user summary',
    message: 'you havent saved an address or credit card',
    code: 'n/a',
    type: 'advisory',
    displayType: 'inline',
};
export const EnrolledSummary = ({ ICDataComplete, onEditDeliveryAddress, onChangeDeliveryAddress, onEditPaymentCard, onChangeHowYouPay, onHandleSetUpPayment, onHandleSetUpAddress, handleWasUnenrolledDuringSession, handleReEnrollment, }) => {
    const { defaultCheckoutPayment } = usePayments();
    const { defaultCheckoutAddress } = useAddresses();
    const { isEnrolled, setIsEnrolled, alertMessage, setAlertMessage, setIsLoading, isLoading, accountData, updatePersonalDetails, } = useInstantCheckout();
    const missingAllInfo = ICDataComplete && !defaultCheckoutAddress && !defaultCheckoutPayment;
    const showNoAddressAlert = ICDataComplete && !defaultCheckoutAddress && defaultCheckoutPayment;
    const showNoPaymentAlert = ICDataComplete && defaultCheckoutAddress && !defaultCheckoutPayment;
    useEffect(() => {
        if (showNoAddressAlert) {
            track(INSTANT_CHECKOUT_POST_PAGE_ERROR, Object.assign(Object.assign({}, defaultPageAnalyticstPayload), { message: 'you havent saved an address' }));
        }
        if (showNoPaymentAlert) {
            track(INSTANT_CHECKOUT_POST_PAGE_ERROR, Object.assign(Object.assign({}, defaultPageAnalyticstPayload), { message: 'you havent saved a payment' }));
        }
        if (missingAllInfo) {
            track(INSTANT_CHECKOUT_POST_PAGE_ERROR, Object.assign(Object.assign({}, defaultPageAnalyticstPayload), { message: 'you havent saved a payment and address' }));
        }
    }, [showNoAddressAlert, showNoPaymentAlert, missingAllInfo]);
    const handleEditDelivery = () => {
        setChangeEnrolledDetailsAnalytics('delivery address', 'edit delivery details');
        onEditDeliveryAddress();
    };
    const handleChangeWhereYouGet = () => {
        setChangeEnrolledDetailsAnalytics('delivery address', 'change where you get it');
        onChangeDeliveryAddress();
    };
    const handleEditCard = () => {
        setChangeEnrolledDetailsAnalytics('payment method', 'edit card details');
        onEditPaymentCard();
    };
    const handleChangeHowYouPay = () => {
        setChangeEnrolledDetailsAnalytics('payment method', 'change how you pay');
        onChangeHowYouPay();
    };
    const handleSetUpAddress = () => {
        setPartialEnrolledAnalytics('address');
        onHandleSetUpAddress();
    };
    const handleSetUpPayment = () => {
        setPartialEnrolledAnalytics('payment');
        onHandleSetUpPayment();
    };
    const handleError = (alert) => {
        setAlertMessage(alert);
    };
    const handleChangeEnrolled = (action) => {
        setAlertMessage(null);
        setToggleEnrollmentAnalytics(action === 're-enroll' ? 'on' : 'off');
        if (accountData) {
            const enrolmentFlag = action === 're-enroll' ? 'Y' : 'N';
            if (enrolmentFlag === 'Y') {
                handleReEnrollment();
                return;
            }
            const data = checkForLoyaltyAndBuildAccountObject(accountData, enrolmentFlag);
            setIsEnrolled(false);
            setIsLoading(true);
            updatePersonalDetails(data)
                .then((res) => {
                setIsEnrolled(res.data.account.profile.enableInstantCheckout === 'Y');
                handleWasUnenrolledDuringSession(res.data.account.profile.enableInstantCheckout === 'N');
                const alert = action === 're-enroll'
                    ? {
                        text: INSTANT_CHECKOUT_ENROLLMENT_SUCCESS.message,
                        alertStatus: TAlertStatus.SUCCESS,
                        alertType: TAlertType.TOP,
                    }
                    : null;
                setAlertMessage(alert);
                b2cAddressesApi.getAllAddresses();
                b2cPaymentsApi.getCreditAndPaypalCards();
            })
                .catch((error) => {
                var _a, _b, _c, _d;
                setIsEnrolled((prevState) => !prevState);
                const errorCode = (_d = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.errorCode;
                setPageInfo('error', 'inline', `toggle on/off`, ERR_GENERIC.message, errorCode || 'n/a');
                handleError({
                    text: ERR_GENERIC.message,
                    alertStatus: TAlertStatus.ERROR,
                    alertType: TAlertType.TOP,
                });
            })
                .finally(() => {
                setIsLoading(false);
            });
        }
    };
    const showUpdatedAdrAlertMsg = (alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.alertType) === TAlertType.IC_ADDRESS_UPDATE;
    const showUpdatedPaymentAlertMsg = (alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.alertType) === TAlertType.IC_PAYMENT_UPDATE;
    return (React.createElement("div", { className: "sui-gap-8 sui-flex sui-flex-col" },
        React.createElement("div", null,
            React.createElement("div", { className: "sui-mb-4" },
                React.createElement(Typography, null, "Buy items in a single step. Choose Delivery when you shop online to purchase with Instant Checkout using your saved information.")),
            React.createElement("div", { className: "sui-mb-6" },
                React.createElement(InstantCheckoutToggle, { isEnrolled: isEnrolled, onChangeToggle: handleChangeEnrolled })),
            (alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.alertType) === TAlertType.TOP && React.createElement(InstantCheckoutAlert, null),
            missingAllInfo && (React.createElement(Alert, { status: "warning" }, INSTANT_CHECKOUT_NO_DEFAULT_ADDRESS_AND_PAYMENT_MSG.message)),
            showNoAddressAlert && (React.createElement(Alert, { status: "warning" }, INSTANT_CHECKOUT_NO_DEFAULT_ADDRESS_MSG.message)),
            showNoPaymentAlert && (React.createElement(Alert, { status: "warning" }, INSTANT_CHECKOUT_NO_DEFAULT_PAYMENT_MSG.message))),
        isLoading ? (React.createElement("div", { className: "sui-max-w-[320px] sui-grid sui-gap-y-12 md:sui-gap-y-14" },
            React.createElement(SkeletonContent, { disablePadding: true },
                React.createElement(SkeletonBlock, { height: 10 }),
                React.createElement(SkeletonBlock, { aspect: "wide", height: 40 })),
            React.createElement(SkeletonContent, { disablePadding: true },
                React.createElement(SkeletonBlock, { height: 10 }),
                React.createElement(SkeletonBlock, { aspect: "wide", height: 40 })))) : (React.createElement("div", { className: "sui-gap-8 sui-flex sui-flex-col" },
            missingAllInfo && (React.createElement(Button, { onClick: handleReEnrollment, "data-testid": "set-up-ic-btn" }, "Set Up Instant Checkout")),
            React.createElement("div", { className: "sui-gap-12 sui-flex sui-flex-col" },
                React.createElement(EnrolledSummarySection, { title: "Delivery Address" }, defaultCheckoutAddress ? (React.createElement(React.Fragment, null,
                    showUpdatedAdrAlertMsg && (React.createElement("div", { className: "sui-col-span-full sui-my-2" },
                        React.createElement(InstantCheckoutAlert, null))),
                    React.createElement(DeliveryAddress, { onEdit: handleEditDelivery, onChangeAddress: handleChangeWhereYouGet }))) : (React.createElement(NoMethodCard, { onSetUp: handleSetUpAddress, ctaText: !missingAllInfo ? 'Set Up Where You Get It' : null, missingMethod: "an address" }))),
                React.createElement(EnrolledSummarySection, { title: "Payment Method" }, defaultCheckoutPayment ? (React.createElement(React.Fragment, null,
                    showUpdatedPaymentAlertMsg && (React.createElement("div", { className: "sui-col-span-full sui-my-2" },
                        React.createElement(InstantCheckoutAlert, null))),
                    React.createElement(PaymentMethod, { defaultCheckoutPayment: defaultCheckoutPayment, onEdit: handleEditCard, onChangePayment: handleChangeHowYouPay }))) : (React.createElement(NoMethodCard, { onSetUp: handleSetUpPayment, ctaText: !missingAllInfo ? 'Set Up How You Pay' : null, missingMethod: "a credit card" }))))))));
};
